import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { CoaCategorySelect } from '@/components/financials/CoaCategorySelect';
import {
  useDeleteProjectionCapital,
  useInsertProjectionCapital,
  useProjectionsCapital,
  useUpdateProjectionCapital,
} from '@/features/projections/hooks';

export const CAPITAL_CATEGORIES = [
  { value: 'equipment', label: 'Equipment' },
  { value: 'investment', label: 'Investment / Capital injection' },
  { value: 'loan', label: 'Loan / Note' },
  { value: 'dividend', label: 'Dividend / Distribution' },
  { value: 'real_estate', label: 'Real estate' },
  { value: 'tax', label: 'Tax payment' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Capital item detail page — the "add / edit item" surface the Capital
 * Planning tab links to. New items use the route `/new`; existing items
 * use `/:id`. An item is a single planned capital transaction: a date, a
 * direction, an amount, a planning category, the Chart-of-Accounts category
 * it posts against, and free-form notes.
 */
export function CapitalItemPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();

  const { data: capital, isLoading } = useProjectionsCapital();
  const insert = useInsertProjectionCapital();
  const update = useUpdateProjectionCapital();
  const del = useDeleteProjectionCapital();

  const existing = useMemo(
    () => (isNew ? null : (capital ?? []).find((r) => r.id === id) ?? null),
    [capital, id, isNew],
  );

  const [txnDate, setTxnDate] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('equipment');
  const [direction, setDirection] = useState<'debit' | 'credit'>('debit');
  const [amount, setAmount] = useState('');
  const [qbAccountId, setQbAccountId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [hydrated, setHydrated] = useState(false);

  // Hydrate the form once the existing row loads (edit mode only).
  useEffect(() => {
    if (isNew || hydrated || !existing) return;
    setTxnDate(existing.txn_date);
    setDescription(existing.description);
    setCategory(existing.category);
    setDirection(existing.direction);
    setAmount(String(existing.amount_cents / 100));
    setQbAccountId(existing.qb_account_id);
    setNotes(existing.notes ?? '');
    setHydrated(true);
  }, [existing, isNew, hydrated]);

  const canSave = txnDate && description && amount;

  const handleSave = () => {
    if (!canSave) return;
    const dollars = Number(amount.replace(/[$,_\s]/g, ''));
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const payload = {
      txn_date: txnDate,
      description,
      category,
      direction,
      amount_cents: Math.round(dollars * 100),
      qb_account_id: qbAccountId,
      notes: notes || null,
    };
    if (isNew) {
      insert.mutate(payload, {
        onSuccess: () => {
          toast.success('Capital item added');
          navigate('/financials/projections?tab=capital');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to save'),
      });
    } else {
      update.mutate(
        { id: id!, ...payload },
        {
          onSuccess: () => {
            toast.success('Capital item updated');
            navigate('/financials/projections?tab=capital');
          },
          onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to save'),
        },
      );
    }
  };

  const handleDelete = () => {
    if (isNew || !id) return;
    if (!confirm(`Delete "${existing?.description ?? 'this item'}"?`)) return;
    del.mutate(id, {
      onSuccess: () => {
        toast.success('Capital item deleted');
        navigate('/financials/projections?tab=capital');
      },
    });
  };

  const saving = insert.isPending || update.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title={isNew ? 'New capital item' : 'Edit capital item'}
        actions={
          <Button variant="outline" onClick={() => navigate('/financials/projections?tab=capital')}>
            <ArrowLeft className="h-4 w-4" />
            Back to plan
          </Button>
        }
      />

      {!isNew && isLoading ? (
        <Skeleton className="h-96" />
      ) : (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Item details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Projected date">
              <Input type="date" value={txnDate} onChange={(e) => setTxnDate(e.target.value)} />
            </Field>
            <Field label="Description">
              <Input
                placeholder="e.g. CNC machine purchase"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Planning category">
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CAPITAL_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Direction">
                <Select value={direction} onValueChange={(v) => setDirection(v as 'debit' | 'credit')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Outflow (money out)</SelectItem>
                    <SelectItem value="credit">Inflow (money in)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Amount (USD)">
                <Input
                  placeholder="0.00"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="Chart-of-Accounts category">
                <CoaCategorySelect value={qbAccountId} onChange={setQbAccountId} />
              </Field>
            </div>
            <Field label="Notes">
              <Input
                placeholder="Optional"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>

            <div className="flex items-center justify-between pt-2">
              {!isNew ? (
                <Button variant="ghost" onClick={handleDelete} className="text-fresh-salmon-dark">
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/financials/projections?tab=capital')}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={!canSave || saving}>
                  {isNew ? 'Add item' : 'Save changes'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
